import { parsePako, stringifyPako } from 'utils/gzip'

import type { ScormRuntimeVersion } from '../transport/common'
import type { ScormSuspendData, IInteractionData } from '../transport/types'
import type {
  CompletionStatus,
  ILmsEvent,
  IProviderConfig,
  IScore,
  LmsType,
  SuccessStatus,
} from '../types'
import { BaseLmsProvider } from './base.provider'

const FLUSH_DELAY_MS = 800

// Per-batch cap mirrors backend zod (`scormEventsSchema.body.events.max(1000)`).
const EVENTS_MAX_PER_BATCH = 1000

// Heartbeat interval: scheduled commit so the latest cmiData reaches the
// backend even when redux goes quiet (e.g. learner reading a long page
// without interacting). 30s is the same cadence used by the legacy
// ScormSession player. Skipped automatically when payload is unchanged
// (lastSentPayload byte-equality check inside flush()).
const HEARTBEAT_INTERVAL_MS = 30_000

type ContentSpaceSessionStatus = 'IN_PROGRESS' | 'COMPLETED' | 'PASSED' | 'FAILED' | 'NOT_STARTED'

type ScormEventType = 'Initialize' | 'Terminate' | 'SetValue' | 'Commit'

interface IScormEventLog {
  eventType: ScormEventType
  element?: string
  value?: string
  oldValue?: string
  timestamp: string
}

export interface ISessionPayload {
  sessionToken?: string
  status?: ContentSpaceSessionStatus
  score?: number
  scoreMin?: number
  scoreMax?: number
  progress?: number
  location?: string
  cmiData?: Record<string, string>
}

export interface IPortalProviderOptions {
  sessionId: string
  sessionToken: string
  initialCmiData: Record<string, string>
  /** Contentspace API base URL (e.g. https://app.parta.io/contentspace). */
  endpoint: string
  /** Session learner email/name. Seeds learnerInfo so the slave XapiProvider's
   *  actor.mbox matches what backend LrsService.queryStatements filters on
   *  (`mailto:<session.email>`). Without this, statements ship as
   *  anonymous@example.com and the admin xAPI tab is empty. */
  email?: string
  userName?: string
  /** Fired when the course terminates (SCORM LMSFinish/Terminate). Gives host
   *  the final payload so it can show a completion dialog without polling. */
  onTerminated?: (payload: ISessionPayload) => void
  /** Fired after each successful commit (PUT /sessions/:id). V1 LMS player relied on
   *  ScormSession snapshot subscription to keep header progress live; V2 inline
   *  player has no such snapshot, so the host listens to this callback instead. */
  onCommit?: (payload: ISessionPayload) => void
}

export class PortalProvider extends BaseLmsProvider {
  readonly type: LmsType = 'portal'

  private sessionId: string
  private sessionToken: string
  private initialCmiData: Record<string, string>
  private localCmiData: Record<string, string>
  private endpoint: string
  private flushTimer: ReturnType<typeof setTimeout> | null = null
  // Serialized last successfully-sent payload — used to skip no-op PUTs when
  // upstream callers (lms.middleware on every redux dispatch) push the same
  // suspend/progress/score snapshot repeatedly without any actual change.
  private lastSentPayload: string | null = null
  private onTerminated?: (payload: ISessionPayload) => void
  private onCommit?: (payload: ISessionPayload) => void
  private email?: string
  private userName?: string
  // Per-call SCORM event log, flushed to /scorm-events alongside each PUT.
  // Required by admin UI (`/scorm-logs`) — backend table stays empty otherwise.
  private eventBuffer: IScormEventLog[] = []
  // Heartbeat + onExit handles, owned across the iframe lifetime.
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null
  private unloadHandler: (() => void) | null = null
  private hostWindow: Window | null = null

  constructor(opts: IPortalProviderOptions) {
    super()
    this.sessionId = opts.sessionId
    this.sessionToken = opts.sessionToken
    this.initialCmiData = opts.initialCmiData
    this.localCmiData = { ...opts.initialCmiData }
    this.endpoint = opts.endpoint
    this.onTerminated = opts.onTerminated
    this.onCommit = opts.onCommit
    this.email = opts.email
    this.userName = opts.userName
  }

  async initialize(_config: IProviderConfig): Promise<boolean> {
    this.connected = true

    // Populate learner info from initial cmiData fields written by the session.
    // Session.email/userName (passed by host) take priority over cmi.* fallbacks
    // — see ctor doc for IPortalProviderOptions.email.
    this.setLearnerInfo({
      learnerId:
        this.email ||
        this.initialCmiData['cmi.learner_id'] ||
        this.initialCmiData['cmi.core.student_id'] ||
        undefined,
      learnerEmail: this.email,
      learnerName:
        this.userName ||
        this.initialCmiData['cmi.learner_name'] ||
        this.initialCmiData['cmi.core.student_name'] ||
        undefined,
      lmsType: 'portal',
      sessionId: this.sessionId,
    })

    this.logEvent({ eventType: 'Initialize' })
    this.attachLifecycle()
    console.info('[PortalProvider] Initialized, sessionId:', this.sessionId)
    return true
  }

  async terminate(): Promise<void> {
    this.logEvent({ eventType: 'Terminate' })
    this.detachLifecycle()
    await this.commit()
    this.connected = false
    const payload = this.buildPayload()
    this.onTerminated?.(payload)
    console.info('[PortalProvider] Terminated')
  }

  // Heartbeat + onExit beacon. Host (PortalV2Player) calls terminate() on
  // unmount (back button), which already awaits a clean PUT — these handlers
  // exist for the cases host can't intercept: close-tab, page refresh, and
  // mobile visibilitychange:hidden (browser may kill in-flight fetch).
  private attachLifecycle(): void {
    if (typeof window === 'undefined') {
      return
    }
    this.hostWindow = window
    this.heartbeatTimer = setInterval(() => {
      // commit() is a no-op on PUT when payload is unchanged (lastSentPayload
      // byte-equality), so an idle learner reading a long page costs nothing.
      this.commit().catch((err) => {
        console.error('[PortalProvider] Heartbeat commit failed:', err)
      })
    }, HEARTBEAT_INTERVAL_MS)

    this.unloadHandler = () => this.commitBeacon()
    window.addEventListener('pagehide', this.unloadHandler)
    // visibilitychange:hidden fires on iOS Safari when the tab is backgrounded
    // (no pagehide there); also useful when user switches apps.
    window.addEventListener('visibilitychange', this.unloadHandler)
  }

  private detachLifecycle(): void {
    if (this.heartbeatTimer !== null) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }
    if (this.hostWindow && this.unloadHandler) {
      this.hostWindow.removeEventListener('pagehide', this.unloadHandler)
      this.hostWindow.removeEventListener('visibilitychange', this.unloadHandler)
    }
    this.hostWindow = null
    this.unloadHandler = null
  }

  // Synchronous flush via navigator.sendBeacon — fetch() doesn't reliably
  // complete when the page is unloading. Mirrors ScormSession.commitBeacon.
  private commitBeacon(): void {
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
      // visibilitychange fired but page is still visible → not an exit, skip.
      return
    }
    if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') {
      return
    }
    if (!this.sessionToken) {
      return
    }
    const payload = this.buildPayload()
    const cmiBlob = new Blob([JSON.stringify(payload)], { type: 'application/json' })
    navigator.sendBeacon(`${this.endpoint}/public/sessions/${this.sessionId}/beacon`, cmiBlob)

    if (this.eventBuffer.length > 0) {
      const events = this.eventBuffer.splice(0, EVENTS_MAX_PER_BATCH)
      const eventsBlob = new Blob([JSON.stringify({ sessionToken: this.sessionToken, events })], {
        type: 'application/json',
      })
      navigator.sendBeacon(
        `${this.endpoint}/public/sessions/${this.sessionId}/scorm-events`,
        eventsBlob,
      )
    }
  }

  // --- Suspend data ---

  async setSuspendData(data: ScormSuspendData): Promise<void> {
    const value = stringifyPako(data)
    this.logSetValue('cmi.suspend_data', value)
    this.localCmiData['cmi.suspend_data'] = value
    this.scheduleFlush()
  }

  async getSuspendData(): Promise<ScormSuspendData | null> {
    return this.getSuspendDataSync()
  }

  // Called synchronously by useReduxUpdate.ts:179 at mount time to restore course state.
  getSuspendDataSync(): ScormSuspendData | null {
    const raw = this.localCmiData['cmi.suspend_data']
    if (!raw) {
      return null
    }
    try {
      return parsePako<ScormSuspendData>(raw)
    } catch {
      return null
    }
  }

  // --- Progress & Score ---

  async setProgress(value: number): Promise<void> {
    const v = (value / 100).toString()
    this.logSetValue('cmi.progress_measure', v)
    this.localCmiData['cmi.progress_measure'] = v
    this.scheduleFlush()
  }

  async setScore(score: IScore): Promise<void> {
    this.logSetValue('cmi.score.min', score.min.toString())
    this.logSetValue('cmi.score.max', score.max.toString())
    this.logSetValue('cmi.score.raw', score.raw.toString())
    this.logSetValue('cmi.score.scaled', score.scaled.toString())
    this.localCmiData['cmi.score.min'] = score.min.toString()
    this.localCmiData['cmi.score.max'] = score.max.toString()
    this.localCmiData['cmi.score.raw'] = score.raw.toString()
    this.localCmiData['cmi.score.scaled'] = score.scaled.toString()
    this.scheduleFlush()
  }

  async setCompletionStatus(status: CompletionStatus): Promise<void> {
    this.logSetValue('cmi.completion_status', status)
    this.localCmiData['cmi.completion_status'] = status
    this.scheduleFlush()
  }

  async setSuccessStatus(status: SuccessStatus): Promise<void> {
    this.logSetValue('cmi.success_status', status)
    this.localCmiData['cmi.success_status'] = status
    this.scheduleFlush()
  }

  // Location is flushed immediately — critical for resume on next session open.
  async setLocation(location: string): Promise<void> {
    this.logSetValue('cmi.location', location)
    this.localCmiData['cmi.location'] = location
    await this.flush()
  }

  async trackEvent(_event: ILmsEvent): Promise<void> {
    // xAPI-style events not supported in portal V2.
  }

  // Stores interaction data into localCmiData so it is included in the next PUT.
  // Mirrors scorm-client.ts:setValueInteraction field mapping.
  setInteraction(index: number, data: IInteractionData): void {
    const prefix = `cmi.interactions.${index}`
    this.setValueLogged(`${prefix}.id`, data.id)
    this.setValueLogged(`${prefix}.type`, data.type)
    if (data.learner_response !== undefined) {
      this.setValueLogged(`${prefix}.learner_response`, data.learner_response)
    }
    if (data.student_response !== undefined) {
      this.setValueLogged(`${prefix}.student_response`, data.student_response)
    }
    this.setValueLogged(`${prefix}.correct_responses.0.pattern`, data.correctAnswer)
    this.setValueLogged(`${prefix}.weighting`, '1')
    this.setValueLogged(`${prefix}.result`, data.result)
    this.setValueLogged(`${prefix}.latency`, data.latency)
    if (data.timestamp !== undefined) {
      this.setValueLogged(`${prefix}.timestamp`, data.timestamp)
    }
    if (data.time !== undefined) {
      this.setValueLogged(`${prefix}.time`, data.time)
    }
    if (data.description !== undefined) {
      this.setValueLogged(`${prefix}.description`, data.description)
    }
    this.scheduleFlush()
  }

  private setValueLogged(element: string, value: string): void {
    this.logSetValue(element, value)
    this.localCmiData[element] = value
  }

  // Portal V2 uses SCORM 2004 semantics (learner_response, richer types).
  getScormVersion(): ScormRuntimeVersion {
    return '2004'
  }

  async commit(): Promise<void> {
    if (this.flushTimer !== null) {
      clearTimeout(this.flushTimer)
      this.flushTimer = null
    }
    this.logEvent({ eventType: 'Commit' })
    await this.flush()
  }

  // --- Internal ---

  private logEvent(event: Omit<IScormEventLog, 'timestamp'> & { timestamp?: string }): void {
    this.eventBuffer.push({
      eventType: event.eventType,
      element: event.element,
      value: event.value,
      oldValue: event.oldValue,
      timestamp: event.timestamp ?? new Date().toISOString(),
    })
  }

  // Captures `oldValue` from current localCmiData BEFORE callers overwrite it.
  // Two anti-noise filters:
  //  - skip true no-op writes (lms.middleware re-emits identical state often).
  //  - collapse runs of SetValue on the same element into a single buffered
  //    record. Required for `cmi.suspend_data`: pako-compressed bytes flip on
  //    every redux tick, producing dozens of admin-log rows per second for
  //    one logical change. We keep the first oldValue + the latest value.
  private logSetValue(element: string, value: string): void {
    const oldValue = this.localCmiData[element]
    if (value === oldValue) {
      return
    }
    const last = this.eventBuffer[this.eventBuffer.length - 1]
    if (last && last.eventType === 'SetValue' && last.element === element) {
      last.value = value
      last.timestamp = new Date().toISOString()
      return
    }
    this.logEvent({ eventType: 'SetValue', element, value, oldValue })
  }

  private async flushEvents(): Promise<void> {
    if (this.eventBuffer.length === 0 || !this.sessionToken) {
      return
    }
    const events = this.eventBuffer.splice(0, EVENTS_MAX_PER_BATCH)
    try {
      const res = await fetch(`${this.endpoint}/public/sessions/${this.sessionId}/scorm-events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ sessionToken: this.sessionToken, events }),
      })
      if (!res.ok) {
        // Re-buffer at the head so original ordering is preserved on next flush.
        this.eventBuffer.unshift(...events)
        console.error('[PortalProvider] POST scorm-events failed:', res.status)
      }
    } catch (err) {
      this.eventBuffer.unshift(...events)
      console.error('[PortalProvider] POST scorm-events error:', err)
    }
  }

  private scheduleFlush() {
    if (this.flushTimer !== null) {
      clearTimeout(this.flushTimer)
    }
    this.flushTimer = setTimeout(() => {
      this.flushTimer = null
      this.flush().catch((err) => {
        console.error('[PortalProvider] Scheduled flush failed:', err)
      })
    }, FLUSH_DELAY_MS)
  }

  private async flush(): Promise<void> {
    if (!this.sessionId) {
      return
    }
    const payload = this.buildPayload()
    const serialized = JSON.stringify(payload)
    // Skip the PUT if payload is byte-identical to last successfully-sent one
    // (without this, idle redux ticks push 30+ no-op PUTs per minute). But
    // still flush scorm-events — they may have accumulated even when cmiData
    // is unchanged (Initialize/Commit/Terminate are no-ops on cmi).
    if (serialized !== this.lastSentPayload) {
      try {
        const res = await fetch(`${this.endpoint}/public/sessions/${this.sessionId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: serialized,
        })
        if (res.ok) {
          this.lastSentPayload = serialized
          this.onCommit?.(payload)
        } else {
          console.error('[PortalProvider] PUT session failed:', res.status)
        }
      } catch (err) {
        console.error('[PortalProvider] PUT session error:', err)
      }
    }
    await this.flushEvents()
  }

  // Mirrors ScormSession.buildSessionPayload() — same status-mapping logic.
  private buildPayload(): ISessionPayload {
    const data = this.localCmiData
    const completionStatus = data['cmi.completion_status']
    const successStatus = data['cmi.success_status']

    let status: ContentSpaceSessionStatus = 'IN_PROGRESS'
    if (completionStatus === 'completed') {
      if (successStatus === 'passed') {
        status = 'PASSED'
      } else if (successStatus === 'failed') {
        status = 'FAILED'
      } else {
        status = 'COMPLETED'
      }
    } else if (successStatus === 'passed') {
      status = 'PASSED'
    } else if (successStatus === 'failed') {
      status = 'FAILED'
    }

    const scoreRaw = data['cmi.score.raw']
    const score = scoreRaw ? Number.parseFloat(scoreRaw) : undefined
    const scoreMin = data['cmi.score.min'] ? Number.parseFloat(data['cmi.score.min']) : undefined
    const scoreMax = data['cmi.score.max'] ? Number.parseFloat(data['cmi.score.max']) : undefined
    const location = data['cmi.location'] || undefined

    let progress: number | undefined
    const progressMeasure = data['cmi.progress_measure']
    if (progressMeasure) {
      const parsed = Number.parseFloat(progressMeasure)
      if (!Number.isNaN(parsed)) {
        progress = Math.round(parsed * 100)
      }
    } else if (status === 'COMPLETED' || status === 'PASSED' || status === 'FAILED') {
      progress = 100
    }

    return {
      sessionToken: this.sessionToken || undefined,
      status,
      score: score !== undefined && !Number.isNaN(score) ? score : undefined,
      scoreMin: scoreMin !== undefined && !Number.isNaN(scoreMin) ? scoreMin : undefined,
      scoreMax: scoreMax !== undefined && !Number.isNaN(scoreMax) ? scoreMax : undefined,
      progress,
      location,
      cmiData: data,
    }
  }
}
